import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Row, Col, Alert, Spinner
} from 'react-bootstrap';
import { FaEdit, FaTrashAlt, FaPlus, FaDownload, FaEye } from 'react-icons/fa';
import { bookingInvoices } from '../../api/apiClient';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/css/CarPrice.css';
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

function BookingInvoices() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [form, setForm] = useState({
    customer_num: '',
    payment_term: '',
    created_at: '',
  });

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });

  const fetchData = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await bookingInvoices.index({
        page,
        per_page: pagination.per_page
      });

      if (response.success) {
        // تأكد من أن البيانات هي مصفوفة
        const dataArray = response.data.data || response.data || [];
        setData(Array.isArray(dataArray) ? dataArray : []);
        
        setPagination(response.data.meta || {
          current_page: page,
          last_page: 1,
          per_page: 10,
          total: Array.isArray(dataArray) ? dataArray.length : 0
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_fetch_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_fetch_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openForm = (entry = null) => {
    if (entry) {
      setForm({
        customer_num: entry.customer_num || '',
        payment_term: entry.payment_term || '',
        created_at: entry.created_at ? entry.created_at.replace(' ', 'T').substring(0, 16) : '',
      });
      setEditData(entry);
    } else {
      setForm({
        customer_num: '',
        payment_term: '',
        created_at: '',
      });
      setEditData(null);
    }
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      const fd = new FormData();
      
      fd.append('customer_num', form.customer_num);
      fd.append('payment_term', form.payment_term);
      fd.append('created_at', form.created_at);

      let response;
      if (editData) {
        response = await bookingInvoices.update(editData.id, fd);
      } else {
        response = await bookingInvoices.store(fd);
      }

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('data_saved_successfully'),
          timer: 2000,
          showConfirmButton: false
        });
        setShowForm(false);
        fetchData(pagination.current_page);
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_save_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_save_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error saving data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();

    if (statusLower === "bezahlt" || statusLower === "paid") {
      return (
        <span className="text-success fw-bold">
          {t("paid")} ✔
        </span>
      );
    } else if (statusLower === "unbezahlt" || statusLower === "unpaid") {
      return (
        <span className="text-danger fw-bold">
          {t("unpaid")} ✖
        </span>
      );
    } else {
      return <span className="text-warning fw-bold">{status}</span>;
    }
  };

  const formatCurrency = (amount, currency = "€") => {
    if (!amount) return `0,00 ${currency}`;
    
    if (typeof amount === 'object') {
      console.warn('Amount is an object:', amount);
      amount = amount.toString();
    }
    
    const cleanAmount = amount.toString().replace(",", ".");
    const number = parseFloat(cleanAmount);

    if (isNaN(number)) return `${amount} ${currency}`;

    return `${number.toFixed(2).replace(".", ",")} ${currency}`;
  };

  // دالة مساعدة لضمان أن القيمة نصية
  const safeDisplay = (value) => {
    if (value === null || value === undefined) return t('not_available');
    if (typeof value === 'object') {
      console.warn('Trying to display object as text:', value);
      return JSON.stringify(value);
    }
    return value;
  };

  const handleDownload = async (invoice) => {
    setDownloading(true);
    setDownloadingId(invoice.id);
    try {

      if (!invoice.invoice_url) {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: t('no_invoice_available'),
          timer: 2000,
          showConfirmButton: false
        });
        return;
      }

      const link = document.createElement("a");
      link.href = invoice.invoice_url;
      link.target = "_blank";
      link.download = `invoice-${invoice.booking_id || invoice.id}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('download_failed'),
        timer: 2000,
        showConfirmButton: false
      });
    } finally {
      setDownloading(false);
      setDownloadingId(null);
    }
  };

  const handleView = (invoice) => {
    console.log("👁️ Viewing invoice:", invoice);
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="dashboard-title">💵{t('booking-invoices')}</h3>
        {/* <Button className='add-btn' onClick={() => openForm()}>
          <FaPlus className="me-1" /> {t('Neu_hinzufügen')}
        </Button> */}
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : data.length === 0 ? (
        <Alert variant="info">{t('no_data_available')}</Alert>
      ) : (
        <div className="table-container shadow-sm rounded">
          <div className='table-responsive'>
            <Table bordered hover className="custom-table mb-0">
              <thead className="table-dark text-center">
                <tr>
                  <th>#</th>
                  <th>{t("booking_id")}</th>
                  <th>{t("user_name")}</th>
                  <th>{t("sub_total")}</th>
                  <th>{t("tax_percentage")}</th>
                  <th>{t("total")}</th>
                  <th>{t("payment_status")}</th>
                  <th>{t("date")}</th>
                  <th>{t("action")}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((entry, idx) => (
                  <tr key={entry.id} className="text-center">
                    <td>{(pagination.current_page - 1) * pagination.per_page + idx + 1}</td>
                    <td>
                      <Button
                        variant="link"
                        className="p-0 ms-1 text-decoration-none"
                        onClick={() => navigate(`/admin/bookings/details/${entry.id}`)}
                      >
                        {safeDisplay(entry.booking_id) || t('not_assigned')}
                      </Button>
                    </td>
                    <td>
                      {entry.user_id !== null ? (
                        <Button
                          variant="link"
                          className="p-0 ms-1 text-decoration-none"
                          onClick={() => navigate(`/admin/customers/details/${entry.user_id}`)}
                        >
                          {safeDisplay(entry.user_name) || t('not_assigned')}
                        </Button>
                      ) : (
                        <span className='ms-1'>
                          {safeDisplay(entry.user_name) || t('not_assigned')}
                        </span>
                      )}
                    </td>
                    <td>{formatCurrency(entry.sub_total)}</td>
                    <td>{safeDisplay(entry.tax_percentage)}</td>
                    <td>{formatCurrency(entry.total)}</td>
                    <td>{getStatusBadge(entry.payment_status)}</td>
                    <td>{safeDisplay(entry.date)}</td>
                    <td>
                      <div className="d-flex justify-content-center gap-2 align-items-center">
                        {/* زر التعديل */}
                        <Button
                          className="add-btn me-2"
                          size="sm"
                          onClick={() => openForm(entry)}
                          title={t("edit")}
                          disabled={loading}
                        >
                          <FaEdit />
                        </Button>
                        
                        {/* زر تحميل الفاتورة */}
                        <Button
                          size="sm"
                          style={{
                            backgroundColor: "#ff7b00",
                            border: "none",
                            borderRadius: "20px",
                            padding: "5px 12px",
                          }}
                          onClick={() => handleDownload(entry)}
                          disabled={downloading && downloadingId === entry.id || !entry.invoice_url}
                          title={t("booking.download_pdf")}
                        >
                          {downloading && downloadingId === entry.id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <FaDownload />
                          )}
                        </Button>
                        
                        {/* زر عرض الفاتورة */}
                        <Button
                          size="sm"
                          style={{
                            backgroundColor: "#28a745",
                            border: "none",
                            borderRadius: "20px",
                            padding: "5px 12px",
                          }}
                          onClick={() => handleView(entry)}
                          disabled={!entry.invoice_url}
                          title={t("viewInvoice")}
                        >
                          <FaEye />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
      {!loading && pagination.total > 0 && (
        <PaginationComponent
          currentPage={pagination.current_page}
          lastPage={pagination.last_page}
          onPageChange={fetchData}
          disabled={loading}
        />
      )}

      {/* Add/Edit Modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editData ? t('edit') : t('add')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3 gy-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("customer_num")}</Form.Label>
                  <Form.Control
                    type="number"
                    name="customer_num"
                    value={form.customer_num}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("payment_term")}</Form.Label>
                  <Form.Control
                    type="number"
                    name="payment_term"
                    value={form.payment_term}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("created_at")}</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="created_at"
                    value={form.created_at}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={loading}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? t('saving') : t('save')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Invoice View Modal */}
      <Modal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-dark text-white">
          <Modal.Title>{t("viewInvoice")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoice && (
            <div className="text-center">
              <h5 className="mb-3">
                {t("booking.Invoice")}: {safeDisplay(selectedInvoice.booking_id)}
              </h5>

              <div
                style={{
                  height: "500px",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                }}
              >
                <iframe
                  src={selectedInvoice.invoice_url}
                  title={`Invoice-${selectedInvoice.booking_id}`}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                />
              </div>

              <div className="mt-3 p-3 bg-light rounded">
                <Row>
                  <Col md={6}>
                    <p>
                      <strong>{t("date")}:</strong>{" "}
                      {safeDisplay(selectedInvoice.date)}
                    </p>
                    <p>
                      <strong>{t("sub_total")}:</strong>{" "}
                      {formatCurrency(selectedInvoice.sub_total)}
                    </p>
                    <p>
                      <strong>{t("customer_num")}:</strong>{" "}
                      {safeDisplay(selectedInvoice.customer_num)}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>
                        {t("payment_status")}:
                      </strong>{" "}
                      {getStatusBadge(selectedInvoice.payment_status)}
                    </p>
                    <p>
                      <strong>{t("total")}:</strong>{" "}
                      {formatCurrency(selectedInvoice.total)}
                    </p>
                    <p>
                      <strong>{t("payment_term")}:</strong>{" "}
                      {safeDisplay(selectedInvoice.payment_term)} {t("days")}
                    </p>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowInvoiceModal(false)}
          >
            {t("close")}
          </Button>
          <Button
            variant="primary"
            onClick={() => selectedInvoice && handleDownload(selectedInvoice)}
            disabled={!selectedInvoice?.invoice_url}
          >
            <FaDownload className="me-2" />
            {t("booking.download_pdf")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default BookingInvoices;